import { useState, type FormEvent, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { confirmPasswordReset } from "../api/auth";
import "./password-reset.css";

export const PasswordResetConfirm = (): JSX.Element => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token") || "";

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            setError("Invalid reset token. Please request a new password reset.");
        }
    }, [token]);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);

        if (newPassword !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }

        if (newPassword.length < 8) {
            setError("Password must be at least 8 characters long");
            return;
        }

        if (!token) {
            setError("Invalid reset token");
            return;
        }

        setIsLoading(true);

        try {
            await confirmPasswordReset(token, newPassword);
            setSuccess(true);
            setTimeout(() => {
                navigate("/login");
            }, 3000);
        } catch (err: any) {
            setError(
                err.response?.data?.message ||
                err.message ||
                "Failed to reset password. The token may be invalid or expired."
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="password-reset-container">
            <div className="password-reset-card">
                <div className="password-reset-header">
                    <h1>Reset Password</h1>
                    <p className="password-reset-subtitle">
                        Enter your new password
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="password-reset-form">
                    {error && (
                        <div className="password-reset-error" role="alert">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="password-reset-success" role="alert">
                            Password reset successful! Redirecting to login...
                        </div>
                    )}

                    <div className="password-reset-field">
                        <label htmlFor="newPassword">New Password</label>
                        <input
                            id="newPassword"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            required
                            autoComplete="new-password"
                            disabled={isLoading || success}
                            minLength={8}
                        />
                        <p className="password-reset-hint">
                            Password must be at least 8 characters long
                        </p>
                    </div>

                    <div className="password-reset-field">
                        <label htmlFor="confirmPassword">Confirm Password</label>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                            required
                            autoComplete="new-password"
                            disabled={isLoading || success}
                            minLength={8}
                        />
                    </div>

                    <button
                        type="submit"
                        className="password-reset-button"
                        disabled={isLoading || success || !token}
                    >
                        {isLoading ? "Resetting..." : "Reset Password"}
                    </button>
                </form>

                <div className="password-reset-link">
                    <Link to="/login">Back to Login</Link>
                </div>
            </div>
        </div>
    );
};